import { FunctionComponent, useEffect, useRef, useState } from "react"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import styled from "styled-components"
import { ArrowBack } from "../../atoms/icons/ArrowBack"
import { User } from "../../interfaces/SaleorTypes"
import H1 from "../../atoms/typography/H1"
import SmallButton from "../../atoms/buttons/SmallButton"
import BodyLarge from "../../atoms/typography/BodyLarge"
import LargeButton from "../../atoms/buttons/LargeButton"
import Subtitle from "../../atoms/typography/Subtitle"
import TextInput from "../../molecules/TextInput"
import BodyMedium from "../../atoms/typography/BodyMedium"
import { Col, Hidden, Row, Visible } from "react-grid-system"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { useAcceptedCountries } from "../../../hooks/useAcceptedCountries"
import Select from "../../molecules/Select"
import { AnimatePresence } from "framer-motion"
import Modal from "../../molecules/Modals/Modal"
import LoginModalContainer from "../../organisms/LoginScreens/LoginModalContainer"
import * as React from "react"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import TextButton from "../../atoms/buttons/TextButton"
import Body from "../../atoms/typography/Body"
import { CheckoutType } from "../../../graphql/mutations/createUnpaidOrder"
import { Link } from "react-router-dom"
import AddressValidationRules from "../../atoms/inputs/AddressValidationRules"
import RegisterAccountFields from "./RegisterAccountOnCheckoutButton"
import ShippingFormFields from "./ShippingFormFields"
import { ShareIcon } from "../../atoms/icons/ShareIcon"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import { captureExceptionWithContext } from "../../../utils/sentry"

interface ShippingFormWidgetProps {
  user?: User
  isBillingSame: boolean
  isShippingFound: boolean
  checkoutType: CheckoutType
  createCheckoutSession: (e: any) => void
  onRadioClick: (e: any) => void
  onCheckoutTypeClick: (type: CheckoutType) => void
  onDirectPayment: (flag: boolean) => void
  setOpenModal: (flag: boolean, view: string) => void
}

const SubHeading = styled.div`
  padding: 1em 0;
`

const rentText = (
  <div>
    Rent your Satcube terminal online, and we will deliver a ready-to-use terminal within 48 hours, including capacity,
    where you need it to enable you to focus on your critical missions, wherever you are. <br />
    <br />
    The price includes: <br />
    <br />
    ・Delivery - we take care of the shipping back and forth to your destination
    <br />
    ・Support 24/7 <br />
    ・Training <br />
    <br />
    Please confirm your billing address and your cart, and our Sales team will get in touch with you very soon to
    finalize the details. <br />
    <br />
    For more information about data prices, terms and coverage, you can read{" "}
    <a
      href={
        "https://satcube.sharepoint.com/sites/website/Delade%20dokument/Forms/AllItems.aspx?id=%2Fsites%2Fwebsite%2FDelade%20dokument%2Fservices%2Finstant%2FMKT%20-%20Product%20-%20Satcube%20rental%2Epdf&parent=%2Fsites%2Fwebsite%2FDelade%20dokument%2Fservices%2Finstant&p=true&ga=1"
      }
      target="__blank"
    >
      <b>here</b>
    </a>{" "}
    or ask our Sales team directly.
  </div>
)

const CustomCheckbox = styled.input`
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  margin: 0;
  width: 24px;
  height: 24px;
  border: 3px solid #616161;
  border-radius: 3px;
  transform: translateY(-0.075em);
  display: grid;
  place-content: center;
  cursor: pointer;

  :hover {
    filter: opacity(0.6);
  }

  ::before {
    content: "";
    width: 12px;
    height: 12px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--form-control-color);
    background-color: #616161;
    transform-origin: bottom left;
    border-radius: 1px;
  }

  :checked::before {
    transform: scale(1);
  }
`

const ShippingFormWidget: FunctionComponent<ShippingFormWidgetProps> = ({
  user,
  isBillingSame,
  checkoutType,
  isShippingFound,
  createCheckoutSession,
  onRadioClick,
  onCheckoutTypeClick,
  onDirectPayment,
  setOpenModal,
  ...props
}) => {
  const [isLeasing, setIsLeasing] = useState<boolean>()
  const [isRenting, setIsRenting] = React.useState(checkoutType === "rental")
  const { countries, error, loading } = useAcceptedCountries()
  const [logIn, setLogIn] = React.useState(false)
  //rental stuff
  const [acceptedRentalTerms, setAcceptedRentalTerms] = React.useState(false)

  const [registeredUser, setRegisteredUser] = useState(false)

  const [unregisteredUserValues, setUnregisteredUserValues] = React.useState<any>({
    "billing-first-name": "",
    "billing-last-name": "",
    "billing-email": "",
    "billing-phone": "",
    "billing-company": "",
    "account-password": "",
    "account-password-rep": "",
    "billing-country": user?.shippingAddress?.country ?? "",
  })

  const [countryCode, setCountryCode] = React.useState(user?.shippingAddress?.country ?? undefined)

  const handleChangeBillingAddressForUnregisteredUser = (event: any) => {
    const name = event.target.name
    const value = event.target.value

    setUnregisteredUserValues((values: any) => ({ ...values, [name]: value }))

    if (name === "billing-country") {
      setCountryCode(value)
    }
  }

  if (error) {
    captureExceptionWithContext(error, "error fetching accepted countries from backend")
  }

  const formRef = useRef<HTMLFormElement>(null)

  useEffect(() => {
    if (checkoutType === "leasing") {
      setIsLeasing(true)
      onDirectPayment(false)
    } else if (checkoutType === "rental") {
      setIsRenting(true)
      onDirectPayment(false)
      setIsLeasing(false)
    } else {
      onDirectPayment(true)
    }
  }, [])

  useEffect(() => {
    const formData = formRef.current?.elements
    if (formData && user) {
      formData["billing-first-name"].value = user.firstName
      formData["billing-last-name"].value = user.lastName
      formData["billing-email"].value = user.email
      formData["billing-phone"].value = user.mobilePhoneNumber
      formData["billing-company"].value = user.organization?.name ?? ""
      formData["billing-city"].value = user.shippingAddress.city
      formData["billing-street-address-1"].value = user.shippingAddress.address1
      formData["billing-street-address-2"].value = user.shippingAddress.address2
      formData["billing-postal-code"].value = user.shippingAddress.zipCode
      formData["billing-country"].value = user.shippingAddress.country
    }
  }, [user])

  if (loading) {
    return <LoadingSpinner />
  }

  return (
    <div>
      <Row justify="between" align="center">
        <Col lg={7}>
          <H1>{isLeasing ? "Apply for leasing" : isRenting ? "Rent" : "Direct purchase"}</H1>
        </Col>
        <Col lg={5} xs={12}>
          <Visible xs md>
            <HorizontalSpacer spacing={2} />
          </Visible>
          <SmallButton
            title="Back to checkout method"
            onClick={() => onCheckoutTypeClick(undefined)}
            leftIcon={<ArrowBack />}
          />
        </Col>
      </Row>
      <HorizontalSpacer spacing={2} />
      <BodyLarge textAlign={isRenting ? "left" : "center"}>
        {isLeasing
          ? "Satcube offers leasing for 24 or 36 months for all our terminals. Leasing price is subject to Credit Control and current interest rates. Full warranty is included during the whole leasing period. "
          : isRenting
            ? rentText
            : "You can buy your Satcube terminals and accessories directly online. The estimated delivery time is 30 days after order confirmation."}
      </BodyLarge>
      {isRenting && (
        <div>
          <HorizontalSpacer spacing={2} />
          <Body>Please confirm your billing address and your cart.</Body>
        </div>
      )}
      <HorizontalSpacer spacing={2} />
      {!user && (
        <div>
          <Row justify="center">
            <Hidden xs>
              <LargeButton title="Log in" onClick={() => setLogIn(true)} />
            </Hidden>
            <Visible xs>
              <PrimaryButton title="Log in" onClick={() => setLogIn(true)} />
            </Visible>
          </Row>
          <HorizontalSpacer spacing={2} />
          <Subtitle textAlign="center">Or</Subtitle>
        </div>
      )}
      <form onSubmit={createCheckoutSession} id="address-form" ref={formRef}>
        <div>
          <fieldset>
            <SubHeading>
              <Subtitle>{user ? "Select billing" : "Register account"}</Subtitle>
            </SubHeading>

            {user ? (
              <ShippingFormFields
                handleChangeBillingAddressForUnregisteredUser={handleChangeBillingAddressForUnregisteredUser}
                countryCode={countryCode}
                user={user}
                isShippingFound={isShippingFound}
                unregisteredUserValues={unregisteredUserValues}
              />
            ) : (
              <>
                <RegisterAccountFields
                  handleChangeBillingAddressForUnregisteredUser={handleChangeBillingAddressForUnregisteredUser}
                  countryCode={countryCode}
                  unregisteredUserValues={unregisteredUserValues}
                  onSuccessfulRegister={() => setRegisteredUser(true)}
                  registeredUser={registeredUser}
                />
              </>
            )}
          </fieldset>
        </div>
        <HorizontalSpacer spacing={2} />

        {(user || registeredUser) && (
          <>
            <SubHeading>
              <Subtitle>Select shipping</Subtitle>
            </SubHeading>
            <div style={{ display: "flex", alignItems: "center" }}>
              <CustomCheckbox
                disabled={isShippingFound}
                type="checkbox"
                name=""
                defaultChecked={isBillingSame}
                id="same-address"
                onClick={(e) => onRadioClick(e)}
              />
              <Spacer spacing={1} />
              <BodyMedium>Same as billing address</BodyMedium>
            </div>

            <HorizontalSpacer spacing={1} />

            {!isBillingSame && (
              <>
                <fieldset>
                  <Row justify="between" align="center">
                    <Col lg={6} xs={12}>
                      <TextInput
                        textFieldTitle="Recipient's first name"
                        disabled={isShippingFound}
                        type="text"
                        name="shipping-first-name"
                        id="shipping-first-name"
                        required
                      />
                    </Col>
                    <Col md={6} xs={12}>
                      <Visible xs>
                        <HorizontalSpacer spacing={4} />
                      </Visible>
                      <TextInput
                        textFieldTitle="Recipient's last name"
                        disabled={isShippingFound}
                        type="text"
                        name="shipping-last-name"
                        id="shipping-last-name"
                        required
                      />
                    </Col>
                  </Row>
                  <HorizontalSpacer spacing={2} />
                  <Row justify="between" align="center">
                    <Col md={6} xs={12}>
                      <TextInput
                        textFieldTitle="Recipient's phone"
                        disabled={isShippingFound}
                        type="text"
                        name="shipping-phone"
                        id="shipping-phone"
                        required
                      />
                    </Col>
                    <Col md={6} xs={12}>
                      <Visible xs>
                        <HorizontalSpacer spacing={4} />
                      </Visible>
                      <TextInput
                        textFieldTitle="Recipient's email"
                        disabled={isShippingFound}
                        type="text"
                        id="shipping-email"
                        name="shipping-email"
                        required
                      />
                    </Col>
                  </Row>{" "}
                  <HorizontalSpacer spacing={2} />
                  <Row justify="between" align="center">
                    <Col md={6} xs={12}>
                      <TextInput
                        textFieldTitle="Shipping address 1"
                        disabled={isShippingFound}
                        type="text"
                        name="shipping-street-address-1"
                        id="shipping-street-address-1"
                        required
                      />
                    </Col>
                    <Col md={6} xs={12}>
                      <Visible xs>
                        <HorizontalSpacer spacing={4} />
                      </Visible>
                      <TextInput
                        textFieldTitle="Shipping address 2 (optional)"
                        disabled={isShippingFound}
                        type="text"
                        name="shipping-street-address-2"
                        id="shipping-street-address-2"
                      />
                    </Col>
                  </Row>{" "}
                  <HorizontalSpacer spacing={2} />
                  <Row justify="between" align="center">
                    <Col md={6} xs={12}>
                      <TextInput
                        textFieldTitle="Postal code"
                        disabled={isShippingFound}
                        type="text"
                        name="shipping-postal-code"
                        id="shipping-postal-code"
                        required
                      />
                    </Col>
                    <Col md={6} xs={12}>
                      <Visible xs>
                        <HorizontalSpacer spacing={4} />
                      </Visible>
                      <TextInput
                        textFieldTitle="City"
                        disabled={isShippingFound}
                        type="text"
                        id="shipping-city"
                        name="shipping-city"
                        required
                      />
                    </Col>
                  </Row>{" "}
                  <HorizontalSpacer spacing={2} />
                  <Row justify="between" align="center">
                    <Col md={6} xs={12}>
                      <Select
                        title="Country"
                        disabled={isShippingFound}
                        options={countries ?? []}
                        id="shipping-country"
                        name="shipping-country"
                        required
                      />
                    </Col>
                  </Row>
                  {countryCode && (
                    <Col md={6} xs={12}>
                      <AddressValidationRules
                        countryCode={countryCode}
                        name="shipping-country-area"
                        id="shipping-country-area"
                        onChange={handleChangeBillingAddressForUnregisteredUser}
                      />
                    </Col>
                  )}
                  <HorizontalSpacer spacing={2} />
                </fieldset>
              </>
            )}

            {!isShippingFound && !isRenting && (
              <div>
                <Row justify="center">
                  <SecondaryButton
                    title={"Calculate shipping"}
                    type="submit"
                    /*disabled={
                  !checkEmailValid(unregisteredUserValues["billing-email"])
                }*/
                  />
                </Row>
              </div>
            )}
            {!isShippingFound && isRenting && (
              <div>
                <div>
                  <HorizontalSpacer spacing={2} />
                  <Row justify="center" align="center">
                    <CustomCheckbox
                      name="rental-terms"
                      type="checkbox"
                      checked={acceptedRentalTerms}
                      onChange={() => setAcceptedRentalTerms(!acceptedRentalTerms)}
                    />
                    <Spacer spacing={1} />
                    <Link to="/rental-terms" target="_blank" rel="noopener noreferrer">
                      <TextButton title="Agree to rental service terms" icon={<ShareIcon />} />
                    </Link>
                  </Row>
                  <HorizontalSpacer spacing={1} />
                  <Row justify="center">
                    <SecondaryButton title={"Request rental"} disabled={!acceptedRentalTerms} type="submit" />
                  </Row>
                </div>
              </div>
            )}
          </>
        )}
      </form>

      <AnimatePresence initial={false} mode="wait" onExitComplete={() => null}>
        {logIn && (
          <Modal open={logIn} onClose={() => setLogIn(!logIn)} backdropColor="#D5E2EAe1" closeIcon>
            <LoginModalContainer onCloseModal={() => setLogIn(false)} />
          </Modal>
        )}
      </AnimatePresence>
    </div>
  )
}

export default ShippingFormWidget
